import React, { useState, useEffect } from 'react';
import { FaServer, FaCheckCircle, FaExclamationTriangle, FaSync } from 'react-icons/fa';

/**
 * Monitor visual del estado del Servicio Auditor XCoin
 * Consulta /api/auditor/status y muestra semáforos.
 */
const BlockchainMonitor = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);

    // API URL - Adjust based on environment like AdminView
    const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:5001' : '';

    const fetchStatus = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_URL}/api/auditor/status`);
            const data = await res.json();

            if (data.success) {
                setStats(data.services);
                setLastUpdate(new Date());
            } else {
                setError(data.error || 'Error desconocido');
            }
        } catch (err) {
            setError('Error de conexión con API');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Auto-refresh every 30 seconds
    useEffect(() => {
        fetchStatus();
        const interval = setInterval(fetchStatus, 30000);
        return () => clearInterval(interval);
    }, []);

    const getStatusColor = (status) => {
        switch (status) {
            case 'SYNCED': return '#4caf50'; // Green
            case 'CATCHING_UP': return '#ff9800'; // Orange
            case 'BACKFILL': return '#2196f3'; // Blue
            case 'ERROR_RPC': return '#f44336'; // Red
            default: return '#9e9e9e'; // Gray
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'SYNCED': return 'Sincronizado';
            case 'CATCHING_UP': return 'Poniéndose al día';
            case 'BACKFILL': return 'Reconstrucción Histórica';
            case 'ERROR_RPC': return 'Error RPC/Red';
            default: return status;
        }
    };

    return (
        <div style={{
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px',
            padding: '15px',
            marginBottom: '20px',
            color: 'white'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaServer color="#00d2ff" /> Auditor de Blockchain (XCoin Sentinel)
                </h3>
                <button
                    onClick={fetchStatus}
                    disabled={loading}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        color: loading ? '#666' : '#00d2ff',
                        cursor: 'pointer'
                    }}
                >
                    <FaSync className={loading ? 'spin' : ''} />
                </button>
            </div>

            {error && (
                <div style={{ color: '#ff6b6b', fontSize: '0.9rem', marginBottom: '10px' }}>
                    <FaExclamationTriangle /> {error}
                </div>
            )}

            {!stats && !error && !loading && <div style={{ fontSize: '0.8rem', color: '#888' }}>Iniciando monitor...</div>}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                {stats && stats.map((svc) => (
                    <div key={svc.name} style={{
                        background: 'rgba(255,255,255,0.05)',
                        padding: '10px',
                        borderRadius: '8px',
                        borderLeft: `4px solid ${getStatusColor(svc.status)}`
                    }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{svc.label}</div>
                        <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: '4px' }}>
                            Bloque: {svc.last_block.toLocaleString()}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                            <span style={{
                                fontSize: '0.7rem',
                                background: getStatusColor(svc.status),
                                color: 'black',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontWeight: 'bold'
                            }}>
                                {getStatusLabel(svc.status)}
                            </span>
                            {svc.lag > 0 && (
                                <span style={{ fontSize: '0.7rem', color: '#ff9800' }}>
                                    LAG: {svc.lag.toLocaleString()}
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {stats && stats.length > 0 && stats[0].current_block > 0 && (
                <div style={{ marginTop: '10px', fontSize: '0.7rem', color: '#666', textAlign: 'right' }}>
                    Bloque BSC Actual: {stats[0].current_block.toLocaleString()}
                </div>
            )}

            <style>{`
                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { 100% { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

export default BlockchainMonitor;
